// =============================================================================
// Service Form Wizard - Creazione/Modifica Servizio in Step
// =============================================================================

import { useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, ChevronLeft, ChevronRight, Plus, Trash2, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { ServiceDefinition } from '../../types';
import { useCreateService, useUpdateService, useResourceTypes } from '../../api/hooks';
import { Button } from './_shared';

// Color presets
const COLOR_PRESETS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
];

// Schema form - tipi lassisti per accettare stringhe da input HTML
// La conversione avviene nel resolver custom
const requirementSchema = z.object({
  resourceTypeId: z.string(),
  quantity: z.union([z.string(), z.number()]),
  isRequired: z.boolean(),
});

const serviceSchemaRaw = z.object({
  name: z.string().min(1, i18n.t('validation.serviceNameRequired', { ns: 'settings' })),
  code: z.string().min(1, i18n.t('validation.serviceCodeRequired', { ns: 'settings' })),
  description: z.string().optional(),
  category: z.string().optional(),
  color: z.string().optional(),
  requiresClient: z.boolean(),
  maxClients: z.union([z.string(), z.number()]),
  durationMinutes: z.union([z.string(), z.number()]),
  bufferBeforeMinutes: z.union([z.string(), z.number()]),
  bufferAfterMinutes: z.union([z.string(), z.number()]),
  basePrice: z.union([z.string(), z.number(), z.null()]).optional(),
  requirements: z.array(requirementSchema),
  // Prenotazione pubblica
  publicBookingEnabled: z.boolean().optional(),
  publicBookingMode: z.enum(['auto', 'approval']).nullable().optional(),
  allowPublicResourceSelection: z.boolean().optional(),
});

// Tipo interno del form (può contenere stringhe)
type ServiceFormDataRaw = z.infer<typeof serviceSchemaRaw>;

// Tipo pulito per API (solo numeri)
interface ServiceFormData {
  name: string;
  code: string;
  description?: string;
  category?: string;
  color?: string;
  requiresClient: boolean;
  maxClients: number;
  durationMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  basePrice?: number | null;
  requirements: Array<{
    resourceTypeId: string;
    quantity: number;
    isRequired: boolean;
  }>;
}

// Estrae i flag publicBooking da ServiceDefinition.config.booking_rules
function readServicePublic(config: unknown): {
  publicBookingEnabled: boolean;
  publicBookingMode: 'auto' | 'approval' | null;
  allowPublicResourceSelection: boolean;
} {
  const rules = (config as Record<string, unknown> | undefined)?.booking_rules as
    | Record<string, unknown>
    | undefined;
  return {
    publicBookingEnabled: Boolean(rules?.publicBookingEnabled),
    publicBookingMode:
      (rules?.publicBookingMode as 'auto' | 'approval' | null | undefined) ?? null,
    allowPublicResourceSelection: Boolean(rules?.allowPublicResourceSelection),
  };
}

// Helper per convertire valori form -> API
const toInt = (val: string | number | null | undefined, defaultVal = 0): number => {
  if (val === '' || val === null || val === undefined) return defaultVal;
  const num = Number(val);
  return isNaN(num) ? defaultVal : Math.round(num);
};

const toFloat = (val: string | number | null | undefined): number | undefined => {
  if (val === '' || val === null || val === undefined) return undefined;
  const num = Number(val);
  return isNaN(num) ? undefined : num;
};

const sanitizeFormData = (data: ServiceFormDataRaw): ServiceFormData => ({
  name: data.name,
  code: data.code,
  description: data.description || undefined,
  category: data.category || undefined,
  color: data.color || undefined, // Nota: il backend potrebbe non supportarlo
  requiresClient: data.requiresClient,
  // Un servizio di gruppo ha senso solo con cliente: senza, forza 1
  maxClients: data.requiresClient ? Math.max(1, toInt(data.maxClients, 1)) : 1,
  durationMinutes: toInt(data.durationMinutes, 30),
  bufferBeforeMinutes: toInt(data.bufferBeforeMinutes, 0),
  bufferAfterMinutes: toInt(data.bufferAfterMinutes, 0),
  basePrice: toFloat(data.basePrice),
  requirements: data.requirements.map(r => ({
    resourceTypeId: r.resourceTypeId,
    quantity: toInt(r.quantity, 1),
    isRequired: r.isRequired,
  })),
});

interface ServiceFormWizardProps {
  service: ServiceDefinition | null;
  onClose: () => void;
}

export function ServiceFormWizard({ service, onClose }: ServiceFormWizardProps) {
  const { t } = useTranslation('settings');
  const [step, setStep] = useState(1);
  const totalSteps = 2;
  const isEditing = !!service;

  const createService = useCreateService();
  const updateService = useUpdateService();
  const { data: resourceTypes } = useResourceTypes();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<ServiceFormDataRaw>({
    resolver: zodResolver(serviceSchemaRaw),
    defaultValues: {
      name: service?.name ?? '',
      code: service?.code ?? '',
      description: service?.description ?? '',
      category: service?.category ?? '',
      color: service?.color ?? COLOR_PRESETS[0],
      requiresClient: service?.requiresClient ?? true,
      maxClients: service?.maxClients ?? 1,
      durationMinutes: service?.durationMinutes ?? 30,
      bufferBeforeMinutes: service?.bufferBeforeMinutes ?? 0,
      bufferAfterMinutes: service?.bufferAfterMinutes ?? 0,
      basePrice: service?.basePrice ?? null,
      requirements: service?.requirements?.map(r => ({
        resourceTypeId: r.resourceTypeId,
        quantity: r.quantity,
        isRequired: r.isRequired,
      })) ?? [],
      ...readServicePublic(service?.config),
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'requirements',
  });

  const selectedColor = watch('color');

  // Popola il form quando si apre in modalità modifica
  useEffect(() => {
    if (service && isEditing) {
      // Reset dei campi scalari
      setValue('name', service.name);
      setValue('code', service.code);
      setValue('description', service.description ?? '');
      setValue('category', service.category ?? '');
      setValue('color', service.color ?? COLOR_PRESETS[0]);
      setValue('requiresClient', service.requiresClient ?? true);
      setValue('maxClients', service.maxClients ?? 1);
      setValue('durationMinutes', service.durationMinutes);
      setValue('bufferBeforeMinutes', service.bufferBeforeMinutes ?? 0);
      setValue('bufferAfterMinutes', service.bufferAfterMinutes ?? 0);
      setValue('basePrice', service.basePrice ?? null);

      const pub = readServicePublic(service.config);
      setValue('publicBookingEnabled', pub.publicBookingEnabled);
      setValue('publicBookingMode', pub.publicBookingMode);
      setValue('allowPublicResourceSelection', pub.allowPublicResourceSelection);

      // Mappa i requirements dal backend al formato del form
      if (service.requirements && service.requirements.length > 0) {
        const mappedRequirements = service.requirements.map(r => ({
          resourceTypeId: r.resourceTypeId,
          quantity: r.quantity,
          isRequired: r.isRequired ?? true,
        }));
        replace(mappedRequirements);
      } else {
        replace([]);
      }
    }
  }, [service, isEditing, setValue, replace]);

  const handleNext = async () => {
    const fieldsToValidate = step === 1 
      ? ['name', 'code', 'durationMinutes'] as const
      : ['requirements'] as const;
    
    const isValid = await trigger(fieldsToValidate);
    if (isValid && step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const onSubmit = async (rawData: ServiceFormDataRaw) => {
    // Salva SOLO dall'ultimo step: un submit involontario (es. Invio in un
    // campo) da uno step intermedio non deve salvare/chiudere il popup.
    // In modifica i requisiti sono già valorizzati, quindi senza questa
    // guardia un submit su step 1 salverebbe e chiuderebbe il wizard.
    if (step < totalSteps) {
      await handleNext();
      return;
    }

    try {
      // Converte stringhe in numeri
      const data = sanitizeFormData(rawData);
      
      // Filtra requirements vuoti (senza resourceTypeId selezionato)
      const validRequirements = (data.requirements || []).filter(
        req => req.resourceTypeId && req.resourceTypeId.trim() !== ''
      );

      if (validRequirements.length === 0) {
        setStep(2);
        return;
      }

      // Costruisci payload per API (escludi color se il backend non lo supporta)
      const payload: Record<string, unknown> = {
        name: data.name,
        code: data.code,
        durationMinutes: data.durationMinutes,
        bufferBeforeMinutes: data.bufferBeforeMinutes,
        bufferAfterMinutes: data.bufferAfterMinutes,
        requiresClient: data.requiresClient,
        maxClients: data.maxClients,
        requirements: validRequirements,
      };
      
      // Aggiungi campi opzionali solo se hanno valore
      if (data.description) payload.description = data.description;
      if (data.category) payload.category = data.category;
      if (data.color) payload.color = data.color;
      if (data.basePrice !== undefined && data.basePrice !== null) {
        payload.basePrice = data.basePrice;
      }

      // Prenotazione pubblica: rifletti i flag in config.booking_rules.
      // Il backend fa deep-merge → manteniamo le altre chiavi (es. min_advance_hours
      // esistenti) inviando solo i campi gestiti dal wizard.
      payload.config = {
        ...(service?.config ?? {}),
        booking_rules: {
          ...((service?.config?.booking_rules as Record<string, unknown> | undefined) ?? {}),
          publicBookingEnabled: rawData.publicBookingEnabled ?? false,
          publicBookingMode: rawData.publicBookingMode ?? null,
          allowPublicResourceSelection: rawData.allowPublicResourceSelection ?? false,
        },
      };
      
      if (isEditing && service) {
        await updateService.mutateAsync({ id: service.id, ...payload } as any);
      } else {
        await createService.mutateAsync(payload as any);
      }
      onClose();
    } catch (error) {
      console.error('Error saving service:', error);
    }
  };

  const addRequirement = () => {
    append({
      resourceTypeId: '',
      quantity: 1,
      isRequired: true,
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isEditing ? t('services.editTitle') : t('services.createTitle')}
              </h3>
              <p className="text-sm text-gray-500">
                {t('services.stepOf', { step, total: totalSteps })}
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label={t('common:buttons.close')}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <StepIndicator
                step={1}
                current={step}
                label={t('services.stepInfo')}
                onClick={step > 1 ? () => setStep(1) : undefined}
              />
              <div className="flex-1 h-1 bg-gray-200 rounded">
                <div
                  className="h-1 bg-emerald-600 rounded transition-all"
                  style={{ width: step >= 2 ? '100%' : '0%' }}
                />
              </div>
              <StepIndicator step={2} current={step} label={t('services.stepRequirements')} />
            </div>
          </div>

          {/* Form Content */}
          <form
            id="service-form"
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={(e) => {
              // Evita il submit implicito del form quando si preme Invio in un
              // input: nel wizard il salvataggio deve avvenire SOLO col pulsante
              // finale, mai per Invio accidentale mentre si compilano i campi.
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                e.preventDefault();
              }
            }}
            className="flex-1 overflow-y-auto"
          >
            <div className="p-6">
              {/* Step 1: Info Base */}
              {step === 1 && (
                <div className="space-y-5">
                  {/* Nome */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('services.nameRequired')}
                    </label>
                    <input
                      type="text"
                      {...register('name')}
                      className="w-full px-3 py-2 input"
                      placeholder={t('services.namePlaceholder')}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  {/* Codice */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('services.codeRequired')}
                    </label>
                    <input
                      type="text"
                      {...register('code')}
                      className="w-full px-3 py-2 input"
                      placeholder={t('services.codePlaceholder')}
                    />
                    {errors.code && (
                      <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
                    )}
                  </div>

                  {/* Descrizione */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('common:fields.description')}
                    </label>
                    <textarea
                      {...register('description')}
                      rows={2}
                      className="w-full px-3 py-2 input"
                      placeholder={t('services.descriptionPlaceholder')}
                    />
                  </div>

                  {/* Categoria */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('services.category')}
                    </label>
                    <input
                      type="text"
                      {...register('category')}
                      className="w-full px-3 py-2 input"
                      placeholder={t('services.categoryPlaceholder')}
                    />
                  </div>

                  {/* Toggle Richiede Cliente */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {t('services.requiresClient')}
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {t('services.requiresClientHint')}
                      </p>
                    </div>
                    <Controller
                      name="requiresClient"
                      control={control}
                      render={({ field }) => (
                        <button
                          type="button"
                          onClick={() => field.onChange(!field.value)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            field.value ? 'bg-emerald-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              field.value ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      )}
                    />
                  </div>

                  {/* Max clienti per appuntamento (eventi di gruppo) */}
                  {watch('requiresClient') && (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('services.maxClients')}
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t('services.maxClientsHint')}
                        </p>
                      </div>
                      <Controller
                        name="maxClients"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            value={field.value}
                            onChange={(e) => field.onChange(Math.max(1, parseInt(e.target.value) || 1))}
                            min={1}
                            max={500}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        )}
                      />
                    </div>
                  )}

                  {/* Colore */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('common:fields.color')}
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="flex flex-wrap gap-2">
                        {COLOR_PRESETS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setValue('color', color)}
                            className={`w-8 h-8 rounded-full transition-transform ${
                              selectedColor === color ? 'ring-2 ring-offset-2 ring-emerald-500 scale-110' : ''
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <input
                        type="color"
                        {...register('color')}
                        className="w-8 h-8 rounded cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Durata e Buffer */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('services.durationRequired')}
                      </label>
                      <Controller
                        name="durationMinutes"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            value={field.value}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                            min={5}
                            step={5}
                            className="w-full px-3 py-2 input"
                          />
                        )}
                      />
                      {errors.durationMinutes && (
                        <p className="mt-1 text-sm text-red-600">{errors.durationMinutes.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('services.bufferBefore')}
                      </label>
                      <Controller
                        name="bufferBeforeMinutes"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            value={field.value}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                            min={0}
                            step={5}
                            className="w-full px-3 py-2 input"
                          />
                        )}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('services.bufferAfter')}
                      </label>
                      <Controller
                        name="bufferAfterMinutes"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            value={field.value}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                            min={0}
                            step={5}
                            className="w-full px-3 py-2 input"
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Prezzo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('services.basePrice')}
                    </label>
                    <Controller
                      name="basePrice"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="number"
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                          min={0}
                          step={0.01}
                          className="w-full px-3 py-2 input"
                          placeholder={t('services.optional')}
                        />
                      )}
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Requirements */}
              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">{t('services.resourceRequirements')}</h4>
                        <p className="text-xs text-gray-500 mt-1">
                          {t('services.resourceRequirementsHint')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={addRequirement}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        {t('services.addRequirement')}
                      </button>
                    </div>

                    {fields.length === 0 ? (
                      <div className="border-2 border-dashed border-amber-300 bg-amber-50 rounded-lg p-8 text-center">
                        <p className="text-amber-700 font-medium mb-3">{t('services.noRequirementsWarning')}</p>
                        <button
                          type="button"
                          onClick={addRequirement}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          {t('services.addFirstRequirement')}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {fields.map((field, index) => (
                          <div
                            key={field.id}
                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex-1">
                              <label className="block text-xs text-gray-500 mb-1">{t('services.resourceType')}</label>
                              <select
                                {...register(`requirements.${index}.resourceTypeId` as const)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                              >
                                <option value="">{t('services.selectType')}</option>
                                {resourceTypes?.map((type) => (
                                  <option key={type.id} value={type.id}>
                                    {type.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="w-24">
                              <label className="block text-xs text-gray-500 mb-1">{t('services.quantity')}</label>
                              <Controller
                                name={`requirements.${index}.quantity` as const}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    type="number"
                                    value={field.value}
                                    onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                                    min={1}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                  />
                                )}
                              />
                            </div>
                            <div className="w-24">
                              <label className="block text-xs text-gray-500 mb-1">{t('services.required')}</label>
                              <Controller
                                name={`requirements.${index}.isRequired` as const}
                                control={control}
                                render={({ field }) => (
                                  <select
                                    value={field.value ? 'yes' : 'no'}
                                    onChange={(e) => field.onChange(e.target.value === 'yes')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                  >
                                    <option value="yes">{t('services.yes')}</option>
                                    <option value="no">{t('services.no')}</option>
                                  </select>
                                )}
                              />
                            </div>
                            <div className="pt-5">
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Public Booking */}
                  <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900">{t('services.publicBooking.title')}</h4>
                    <p className="text-xs text-gray-500 -mt-1">{t('services.publicBooking.description')}</p>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <Controller
                        name="publicBookingEnabled"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            checked={!!field.value}
                            onChange={field.onChange}
                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                          />
                        )}
                      />
                      <span className="text-sm text-gray-800">{t('services.publicBooking.enabledLabel')}</span>
                    </label>

                    {watch('publicBookingEnabled') && (
                      <div className="space-y-3 pl-6">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('services.publicBooking.modeLabel')}
                          </label>
                          <Controller
                            name="publicBookingMode"
                            control={control}
                            render={({ field }) => (
                              <select
                                value={field.value ?? ''}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value === ''
                                      ? null
                                      : (e.target.value as 'auto' | 'approval'),
                                  )
                                }
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-emerald-500"
                              >
                                <option value="">{t('services.publicBooking.modeInherit')}</option>
                                <option value="auto">{t('services.publicBooking.modeAuto')}</option>
                                <option value="approval">{t('services.publicBooking.modeApproval')}</option>
                              </select>
                            )}
                          />
                        </div>

                        <label className="flex items-start gap-2 cursor-pointer">
                          <Controller
                            name="allowPublicResourceSelection"
                            control={control}
                            render={({ field }) => (
                              <input
                                type="checkbox"
                                checked={!!field.value}
                                onChange={field.onChange}
                                className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                              />
                            )}
                          />
                          <span className="text-sm text-gray-800">
                            <span className="font-medium">{t('services.publicBooking.allowResourceSelectionLabel')}</span>
                            <span className="block text-xs text-gray-500 mt-0.5">
                              {t('services.publicBooking.allowResourceSelectionHint')}
                            </span>
                          </span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-emerald-900 mb-2">{t('services.summary')}</h4>
                    <div className="text-sm text-emerald-800 space-y-1">
                      <p><strong>{t('common:fields.name')}:</strong> {watch('name') || '-'}</p>
                      <p><strong>{t('common:fields.duration')}:</strong> {watch('durationMinutes')} {t('services.minutes')}</p>
                      <p><strong>{t('services.stepRequirements')}:</strong> {fields.length === 0 ? t('services.noRequirementsCount') : `${fields.length} ${t('services.requirementsSuffix')}`}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
              <Button
                type="button"
                variant="secondary"
                icon={ChevronLeft}
                onClick={step === 1 ? onClose : handleBack}
              >
                {step === 1 ? t('common:buttons.cancel') : t('common:buttons.back')}
              </Button>

              <div className="flex items-center gap-2">
                {step < totalSteps ? (
                  /* Step intermedi: avanti.
                     key distinta dal bottone submit: senza, React riusa lo stesso
                     nodo DOM e al cambio step il browser esegue l'azione di default
                     del click su un nodo diventato type="submit" → salvataggio e
                     chiusura involontari del wizard (bug "il popup si chiude"). */
                  <Button key="wizard-next" type="button" onClick={handleNext}>
                    {t('common:buttons.next')}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  /* Ultimo step: salva */
                  <Button
                    key="wizard-submit"
                    type="submit"
                    form="service-form"
                    loading={isSubmitting}
                    disabled={fields.length === 0}
                    icon={Check}
                  >
                    {isEditing ? t('common:buttons.saveChanges') : t('services.createTitle')}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Step Indicator Component
// =============================================================================

interface StepIndicatorProps {
  step: number;
  current: number;
  label: string;
  onClick?: () => void;
}

function StepIndicator({ step, current, label, onClick }: StepIndicatorProps) {
  const isActive = step === current;
  const isCompleted = step < current;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`flex items-center gap-2 ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
          isCompleted
            ? 'bg-emerald-500 text-white'
            : isActive
            ? 'bg-emerald-600 text-white'
            : 'bg-gray-200 text-gray-600'
        }`}
      >
        {isCompleted ? <Check className="w-4 h-4" /> : step}
      </div>
      <span className={`text-sm font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>
        {label}
      </span>
    </button>
  );
}
